import {
    COUNTRIES,
    type UserInvestmentProfile,
} from '@fruitful/investment-management';
import { useEffect, useMemo, useState } from 'react';
import { Keyboard } from 'react-native';
import {
    BodyMediumRegular,
    Button,
    ButtonVariant,
    Input,
    RadioInputRow,
    SearchIcon,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';
import { useDebouncedCallback } from 'use-debounce';

import { useTrackViewOnFocus } from '../../events/hooks/useTrackViewOnFocus';

const MAX_RESULTS = 5;
const SEARCH_DEBOUNCE_MS = 250;
/* ISO 3166-1 alpha-2 for United States of America - pinned first when it matches. */
const US_CITIZENSHIP_CODE = 'US';

interface CountrySelectionStepProps {
    question: string;
    initialValue?: string;
    onSelect: (updates: Partial<UserInvestmentProfile>) => Promise<void>;
    isPending?: boolean;
}

export const CountrySelectionStep = ({
    question,
    initialValue,
    onSelect,
    isPending,
}: CountrySelectionStepProps) => {
    useTrackViewOnFocus('Profile/CountrySelectionStep');
    const [selectedCode, setSelectedCode] = useState<string | null>(
        initialValue ?? null,
    );
    const [search, setSearch] = useState(() =>
        initialValue != null
            ? (COUNTRIES.find((c) => c.code === initialValue)?.name ?? '')
            : '',
    );
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const updateDebouncedSearch = useDebouncedCallback((term: string) => {
        setDebouncedSearch(term);
    }, SEARCH_DEBOUNCE_MS);

    useEffect(() => {
        if (search.length === 0) {
            updateDebouncedSearch.cancel();
            setDebouncedSearch('');
        } else {
            updateDebouncedSearch(search);
        }
    }, [search, updateDebouncedSearch]);

    useEffect(() => {
        if (initialValue == null) {
            return;
        }
        setSelectedCode(initialValue);
        const name = COUNTRIES.find((c) => c.code === initialValue)?.name;
        if (name != null) {
            setSearch(name);
        }
    }, [initialValue]);

    useEffect(() => {
        if (selectedCode != null) {
            Keyboard.dismiss();
        }
    }, [selectedCode]);

    const filteredCountries = useMemo(() => {
        if (debouncedSearch.length === 0) return [];
        const lower = debouncedSearch.toLowerCase();
        const matches = COUNTRIES.filter(
            (c) =>
                c.nameLower.includes(lower) ||
                c.code.toLowerCase().includes(lower),
        );
        const usIndex = matches.findIndex(
            (c) => c.code === US_CITIZENSHIP_CODE,
        );
        let ordered: typeof matches;
        if (usIndex === -1) {
            ordered = matches;
        } else {
            const us = matches[usIndex];
            ordered =
                us == null
                    ? matches
                    : [
                          us,
                          ...matches.slice(0, usIndex),
                          ...matches.slice(usIndex + 1),
                      ];
        }
        return ordered.slice(0, MAX_RESULTS);
    }, [debouncedSearch]);

    return (
        <>
            <YStack flex={1} gap={16} paddingVertical={16}>
                <BodyMediumRegular>{question}</BodyMediumRegular>
                {/* Prevent dynamic border width changes on focus from clipping */}
                <XStack paddingHorizontal={2}>
                    <Input
                        placeholder="Search for a country"
                        value={search}
                        onChangeText={(text) => {
                            setSearch(text);
                            if (selectedCode != null) {
                                setSelectedCode(null);
                            }
                        }}
                        leftIcon={SearchIcon}
                    />
                </XStack>
                {selectedCode == null && filteredCountries.length > 0 && (
                    <YStack>
                        {filteredCountries.map((country) => (
                            <RadioInputRow
                                key={country.code}
                                label={country.name}
                                isSelected={false}
                                onPress={() => {
                                    setSelectedCode(country.code);
                                    setSearch(country.name);
                                }}
                            />
                        ))}
                    </YStack>
                )}

                <Button
                    text="Continue"
                    variant={ButtonVariant.PRIMARY}
                    disabled={selectedCode == null || isPending}
                    onPress={() => {
                        if (selectedCode != null) {
                            void onSelect({ citizenship: selectedCode });
                        }
                    }}
                    marginTop="auto"
                />
            </YStack>

            {isPending && (
                <YStack
                    alignItems="center"
                    justifyContent="center"
                    width="100%"
                    height="100%"
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    backgroundColor="$white"
                    opacity={0.6}
                >
                    <Spinner />
                </YStack>
            )}
        </>
    );
};
